import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx-js-style";
import { Link } from "react-router-dom";
import { db } from "../firebase";
import { months, subfields } from "../constants";

const registrationHeaders = [
  { key: "firstName", label: "First name" },
  { key: "surname", label: "Surname" },
  { key: "email", label: "Email" },
  { key: "affiliation", label: "Affiliation" },
  { key: "wasInvited", label: "Was invited to dinner" },
  { key: "willAttendDinner", label: "Will attend dinner" },
  { key: "willAttendReception", label: "Will attend reception" },
  { key: "dietaryRequests", label: "Dietary requests" },
  { key: "createdAt", label: "Registration date (CET)" },
];

const formatDateInCET = (timestamp) => {
  const date = new Date(timestamp * 1000);
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Brussels",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(date);
};

const formatRegistrationValue = (value) => {
  if (value === undefined || value === null || value === "") return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (value && typeof value === "object" && value.seconds) {
    return formatDateInCET(value.seconds);
  }
  return value;
};

const exportRowsToXlsx = (rows, filename) => {
  if (!rows.length) return;

  const headers = Object.keys(rows[0]);
  const sheetData = [
    headers,
    ...rows.map((row) => headers.map((header) => row[header] ?? "")),
  ];
  const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

  const range = XLSX.utils.decode_range(worksheet["!ref"]);
  for (let col = range.s.c; col <= range.e.c; col += 1) {
    const headerCellRef = XLSX.utils.encode_cell({ r: 0, c: col });
    if (!worksheet[headerCellRef]) {
      worksheet[headerCellRef] = { t: "s", v: headers[col] };
    }
    worksheet[headerCellRef].s = { font: { bold: true } };
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

export default function Dashboard() {
  const [submissionsData, setSubmissionsData] = useState([]);
  const [registrationsData, setRegistrationsData] = useState([]);
  document.title = "Rousseeuw Prize Dashboard";

  let date = new Date();
  let month = months[date.getMonth()];
  let year = date.getFullYear();
  let day = date.getDate();

  useEffect(() => {
    fetchSubmissionsData();
    fetchRegistrationsData();
  }, []);

  const fetchSubmissionsData = async () => {
    const response = db.collection("2026");
    const data = await response.get();
    setSubmissionsData(
      data.docs.map((item) => ({ ...item.data(), id: item.id })),
    );
  };

  const fetchRegistrationsData = async () => {
    const response = db
      .collection("registrations")
      .orderBy("createdAt", "desc");
    const data = await response.get();

    const sortedRegistrations = data.docs.map((item) => {
      const record = item.data();
      return {
        ...record,
        id: item.id,
        wasInvited:
          record.invitedToDinner === true
            ? "Yes"
            : record.invitedToDinner === false
              ? "No"
              : "",
        willAttendDinner:
          record.attendDinner === true
            ? "Yes"
            : record.attendDinner === false
              ? "No"
              : "",
        willAttendReception:
          record.attendReception === true
            ? "Yes"
            : record.attendReception === false
              ? "No"
              : "",
        createdAt:
          record.createdAt && record.createdAt.seconds
            ? formatDateInCET(record.createdAt.seconds)
            : "",
      };
    });

    setRegistrationsData(sortedRegistrations);
  };

  const submissionsExportRows = submissionsData.map((item) => ({
    ID: item.id,
    Title: item.title,
    Date: item.timestamp
      ? new Date(item.timestamp.seconds * 1000).toLocaleDateString("en-GB")
      : "",
    Category: subfields[item.category],
    "Nominator Name": item.nominator?.name || "",
    "Nominator Affiliation": item.nominator?.affiliation || "",
    "Nominator Email": item.nominator?.email || "",
    "Nominee 1 Name": item.contributors?.[0]?.name || "",
    "Nominee 1 Affiliation": item.contributors?.[0]?.affiliation || "",
    "Nominee 1 Email": item.contributors?.[0]?.email || "",
    "Nominee 2 Name": item.contributors?.[1]?.name || "",
    "Nominee 2 Affiliation": item.contributors?.[1]?.affiliation || "",
    "Nominee 2 Email": item.contributors?.[1]?.email || "",
    "Nominee 3 Name": item.contributors?.[2]?.name || "",
    "Nominee 3 Affiliation": item.contributors?.[2]?.affiliation || "",
    "Nominee 3 Email": item.contributors?.[2]?.email || "",
    "Nominee 4 Name": item.contributors?.[3]?.name || "",
    "Nominee 4 Affiliation": item.contributors?.[3]?.affiliation || "",
    "Nominee 4 Email": item.contributors?.[3]?.email || "",
    "Nominee 5 Name": item.contributors?.[4]?.name || "",
    "Nominee 5 Affiliation": item.contributors?.[4]?.affiliation || "",
    "Nominee 5 Email": item.contributors?.[4]?.email || "",
  }));

  const registrationsExportRows = registrationsData.map((item) => {
    const row = {};
    registrationHeaders.forEach(({ key, label }) => {
      row[label] = formatRegistrationValue(item[key]);
    });
    return row;
  });

  return (
    <div style={{ width: "90%", alignSelf: "center" }}>
      <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() =>
            exportRowsToXlsx(
              submissionsExportRows,
              `RP-${day}_${month}_${year}`,
            )
          }
        >
          Download submissions
        </button>
        <button
          type="button"
          className="btn btn-success"
          onClick={() =>
            exportRowsToXlsx(
              registrationsExportRows,
              `Registrations-${day}_${month}_${year}`,
            )
          }
        >
          Download registrations
        </button>
      </div>

      <div className="table-responsive">
        <table className="table table-striped mt-4" id="submissions-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Title</th>
              <th>Date</th>
              <th>Category</th>
              <th>Nominator Name</th>
              <th className="hidden">Nominator Affiliation</th>
              <th className="hidden">Nominator Email</th>
              <th className="hidden">Nominee 1 Name</th>
              <th className="hidden">Nominee 1 Affiliation</th>
              <th className="hidden">Nominee 1 Email</th>
              <th className="hidden">Nominee 2 Name</th>
              <th className="hidden">Nominee 2 Affiliation</th>
              <th className="hidden">Nominee 2 Email</th>
              <th className="hidden">Nominee 3 Name</th>
              <th className="hidden">Nominee 3 Affiliation</th>
              <th className="hidden">Nominee 3 Email</th>
              <th className="hidden">Nominee 4 Name</th>
              <th className="hidden">Nominee 4 Affiliation</th>
              <th className="hidden">Nominee 4 Email</th>
              <th className="hidden">Nominee 5 Name</th>
              <th className="hidden">Nominee 5 Affiliation</th>
              <th className="hidden">Nominee 5 Email</th>
            </tr>
          </thead>
          <tbody>
            {submissionsData.map((item) => (
              <tr key={item.id}>
                <td>
                  <Link to={`/nomination/${item.id}`}>{item.id}</Link>
                </td>
                <td>{item.title}</td>
                <td>
                  {new Date(item.timestamp.seconds * 1000).toLocaleDateString(
                    "nl-BE",
                  )}
                </td>
                <td>{subfields[item.category]}</td>
                <td>{item.nominator.name}</td>
                <td className="hidden">{item.nominator.affiliation}</td>
                <td className="hidden">{item.nominator.email}</td>
                {item.contributors.map((contributor, index) => (
                  <React.Fragment key={`${item.id}-contributor-${index}`}>
                    <td className="hidden">{contributor.name}</td>
                    <td className="hidden">{contributor.affiliation}</td>
                    <td className="hidden">{contributor.email}</td>
                  </React.Fragment>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="table-responsive">
        <table className="table table-striped mt-4" id="registrations-table">
          <thead>
            <tr>
              {registrationHeaders.map((header) => (
                <th key={header.key}>{header.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {registrationsData.map((item) => (
              <tr key={item.id}>
                {registrationHeaders.map((header) => (
                  <td key={`${item.id}-${header.key}`}>
                    {formatRegistrationValue(item[header.key])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
